<script setup lang="ts">
/**
 * Pegar la credencial del servicio de IA que manda, sin salir del tablero.
 *
 * Un solo diálogo para todos, porque lo que cambia entre un servicio y otro son
 * los textos, y los textos vienen del servidor: cómo se llama el secreto, si sale
 * de un comando o de un tablero web, dónde se consigue. Así, el día que Chuy
 * hable con otro servicio, esta pantalla ya sabe pedirle lo suyo.
 *
 * El secreto nunca vuelve del servidor: de regreso solo viaja una pista
 * enmascarada, igual que las llaves de API.
 */
import { computed, ref, watch } from "vue";
import type { SesionIaDto } from "@chuy/types";
import AppButton from "./AppButton.vue";
import AppIcon from "./AppIcon.vue";
import AppModal from "./AppModal.vue";
import { api } from "@/api/client";
import { useToast } from "@/composables/useToast";

const props = defineProps<{ open: boolean; sesion: SesionIaDto | null }>();
const emit = defineEmits<{ close: []; changed: [sesion: SesionIaDto] }>();

const toast = useToast();
const secreto = ref("");
const guardando = ref(false);
const abriendo = ref(false);

/** Con comando se saca de una terminal; sin él, de un tablero web. */
const conComando = computed(() => Boolean(props.sesion?.comandoToken));
const etiqueta = computed(() => props.sesion?.etiqueta || "la credencial");
const servicio = computed(() => props.sesion?.proveedor.displayName ?? "el servicio de IA");

watch(
  () => props.open,
  (open) => {
    if (open) secreto.value = "";
  },
);

async function guardar(): Promise<void> {
  if (!secreto.value.trim()) return;
  guardando.value = true;
  try {
    const sesion = await api.system.guardarCredencial(secreto.value.trim());
    secreto.value = "";
    toast.success("Guardada y estrenada. Chuy ya puede chambear.");
    emit("changed", sesion);
    emit("close");
  } catch (cause) {
    // El servidor ya la probó contra el servicio, así que este mensaje dice si no
    // la aceptaron y no nomás "no se pudo guardar".
    toast.error(cause instanceof Error ? cause.message : "No pude guardarla");
  } finally {
    guardando.value = false;
  }
}

async function quitar(): Promise<void> {
  guardando.value = true;
  try {
    const sesion = await api.system.quitarCredencial();
    toast.info("Quitada. Vuelvo a usar lo que diga el entorno.");
    emit("changed", sesion);
  } catch (cause) {
    toast.error(cause instanceof Error ? cause.message : "No pude quitarla");
  } finally {
    guardando.value = false;
  }
}

/** El atajo: la terminal con el comando que *saca* el secreto. */
async function abrirTerminal(): Promise<void> {
  abriendo.value = true;
  try {
    await api.system.abrirTerminal("token");
    toast.info("Te abrí la Terminal. Copia lo que te dé y pégalo aquí.");
  } catch (cause) {
    toast.error(cause instanceof Error ? cause.message : "No pude abrir la Terminal");
  } finally {
    abriendo.value = false;
  }
}
</script>

<template>
  <AppModal
    :open="open"
    :title="conComando ? 'Una credencial que no caduca' : `Pega ${etiqueta}`"
    :subtitle="`Para ${servicio}. Se guarda en tu máquina y la pruebo antes de dejarla.`"
    @close="emit('close')"
  >
    <div class="space-y-4 pb-4">
      <ol class="space-y-2.5 text-[13px] leading-relaxed text-secondary">
        <li class="flex gap-2.5">
          <span
            class="mt-px grid size-5 shrink-0 place-items-center rounded-full bg-surface-muted text-[11px] font-semibold text-secondary"
          >
            1
          </span>
          <span class="min-w-0">
            <template v-if="conComando">
              Corre este comando en una terminal:
              <code class="ml-1 rounded-[6px] bg-surface-muted px-1.5 py-0.5 font-mono text-[12px] wrap-anywhere">
                {{ sesion?.comandoToken }}
              </code>
              <AppButton
                v-if="sesion?.puedeSacarConTerminal"
                size="sm"
                variant="ghost"
                icon="external"
                class="ml-1 align-middle"
                :loading="abriendo"
                @click="abrirTerminal"
              >
                Ábremela
              </AppButton>
            </template>
            <template v-else>
              Saca {{ etiqueta }} en
              <a
                v-if="sesion?.dondeSacarlo"
                :href="sesion.dondeSacarlo"
                target="_blank"
                rel="noreferrer"
                class="font-medium wrap-anywhere text-accent underline underline-offset-2"
              >
                {{ sesion.dondeSacarlo.replace(/^https?:\/\//, "") }}
              </a>
              <span v-else>el tablero de {{ servicio }}</span>.
            </template>
          </span>
        </li>
        <li class="flex gap-2.5">
          <span
            class="mt-px grid size-5 shrink-0 place-items-center rounded-full bg-surface-muted text-[11px] font-semibold text-secondary"
          >
            2
          </span>
          <span>Cópiala y pégala aquí abajo. La pruebo antes de guardarla.</span>
        </li>
      </ol>

      <div
        v-if="sesion?.credencial"
        class="flex items-center justify-between gap-3 rounded-[10px] border border-separator bg-surface-muted px-3 py-2.5"
      >
        <span class="min-w-0 text-[12.5px] text-secondary">
          Ya hay una guardada:
          <span class="font-mono wrap-anywhere text-content">{{ sesion.credencial.hint }}</span>
        </span>
        <AppButton size="sm" variant="danger" :loading="guardando" @click="quitar">
          Quitarla
        </AppButton>
      </div>

      <form class="space-y-2" @submit.prevent="guardar">
        <label class="block text-[12px] font-medium text-secondary" for="credencial-ia">
          {{ etiqueta.charAt(0).toUpperCase() + etiqueta.slice(1) }}
        </label>
        <input
          id="credencial-ia"
          v-model="secreto"
          type="password"
          class="field font-mono text-[12.5px]"
          placeholder="pégala aquí"
          autocomplete="off"
          spellcheck="false"
        />
        <p class="flex items-start gap-1.5 text-[11.5px] leading-relaxed text-tertiary">
          <AppIcon name="settings" :size="12" class="mt-0.5 shrink-0" />
          Se guarda en el servidor, en tu propia máquina, con permisos de solo-dueño. No sale de
          ahí ni vuelve a la pantalla.
        </p>
      </form>
    </div>

    <template #footer>
      <AppButton variant="ghost" @click="emit('close')">Ahorita no</AppButton>
      <AppButton
        variant="primary"
        icon="check"
        :loading="guardando"
        :disabled="!secreto.trim()"
        @click="guardar"
      >
        Guardar y probar
      </AppButton>
    </template>
  </AppModal>
</template>
